import { uintStream, commit, shuffle } from '../../lib/originals-vendor/engine.mjs';
import { resolve as plinko } from '../../lib/originals-vendor/plinko.mjs';
import { resolve as limbo } from '../../lib/originals-vendor/limbo.mjs';
import { resolve as dice } from '../../lib/originals-vendor/dice.mjs';
import { resolve as mines } from '../../lib/originals-vendor/mines.mjs';
import plinkoTable from '../../lib/originals-vendor/plinko-paytable.json';
import limboTable from '../../lib/originals-vendor/limbo-paytable.json';
import diceTable from '../../lib/originals-vendor/dice-paytable.json';
import minesTable from '../../lib/originals-vendor/mines-paytable.json';
import { calculateBlackjackCards } from '../blackjack/blackjackVerify';

export const FAIR_GAMES = ['plinko', 'limbo', 'dice', 'mines', 'keno', 'blackjack'];
export const resolvers = { plinko, limbo, dice, mines };
export const tables = { plinko: plinkoTable, limbo: limboTable, dice: diceTable, mines: minesTable };
export const gameName = (id) => id[0].toUpperCase() + id.slice(1);
export const roundGame = (round) => round?.game || 'keno';
export const settledRound = (round) => !!round && (roundGame(round) === 'keno' || round.finished === true);
export const latestRound = (game, type) => {
  const eligible = (round) => settledRound(round) && roundGame(round) === type && !game.hiddenHistoryIds?.has(round.request_id);
  if (eligible(game.round)) return game.round;
  return [...(game.wallet?.rounds || []), ...(game.wallet?.recent?.[type] || [])].filter(eligible)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0] || null;
};

export const fieldsForRound = (round, type, serverSeed = '') => ({
  game: type, clientSeed: round?.client_seed || '', serverSeed, nonce: String(round?.nonce ?? 0),
  risk: round?.params?.risk || 'medium', rows: String(round?.params?.rows || 16), mines: String(round?.params?.mines || 3),
});
export const inputError = (fields) => {
  if (!FAIR_GAMES.includes(fields.game)) return 'Choose an eligible Original.';
  if (fields.clientSeed && !/^[A-Za-z0-9_-]{1,64}$/.test(fields.clientSeed)) return 'Client seed must contain 1–64 letters, numbers, underscores or hyphens.';
  if (fields.serverSeed && (!fields.serverSeed.trim() || fields.serverSeed.length > 256)) return 'Enter a server seed containing 1–256 characters.';
  if (fields.nonce !== '' && (!/^\d+$/.test(fields.nonce) || !Number.isSafeInteger(Number(fields.nonce)))) return 'Nonce must be a whole number between 0 and 9007199254740991.';
  if (fields.game === 'plinko' && (!/^(?:[89]|1[0-6])$/.test(fields.rows) || !['low', 'medium', 'high', 'expert'].includes(fields.risk))) return 'Choose a Plinko risk and 8–16 rows.';
  if (fields.game === 'mines' && !/^(?:[1-9]|1\d|2[0-4])$/.test(fields.mines)) return 'Choose between 1 and 24 mines.';
  return '';
};

// Outcome-only calculation uses the same frozen resolvers as settlement. The
// nominal wager/targets are not displayed as a payout or a verified real bet.
export const calculateOutcome = async (fields) => {
  const { game, serverSeed, clientSeed } = fields, nonce = Number(fields.nonce);
  const hash = await commit(serverSeed);
  if (game === 'blackjack') return { hash, cards: await calculateBlackjackCards(serverSeed, clientSeed, nonce) };
  const count = game === 'keno' ? 39 : game === 'mines' ? 24 : game === 'plinko' ? Number(fields.rows) : 1;
  const uints = await uintStream(serverSeed, clientSeed, nonce, count);
  if (game === 'keno') return { hash, drawn: shuffle(40, uints).slice(0, 10).map((n) => n + 1).sort((a, b) => a - b) };
  const params = { plinko: { rows: Number(fields.rows), risk: fields.risk }, mines: { mines: Number(fields.mines), picks: [] }, limbo: { target_e8: 100000000 }, dice: { target: 5000, direction: 'over' } }[game];
  return { hash, uints, ...resolvers[game](uints, params, tables[game], { betMinor: 100 }) };
};

export const matchesRoundInputs = (fields, round) => settledRound(round) && fields.game === roundGame(round)
  && fields.clientSeed === round.client_seed && fields.nonce !== '' && Number(fields.nonce) === round.nonce
  && (fields.game !== 'plinko' || (fields.risk === round.params.risk && Number(fields.rows) === round.params.rows))
  && (fields.game !== 'mines' || Number(fields.mines) === round.params.mines);